import { v4 as uuidv4 } from 'uuid'
import { readFbCookies, readGaClientId } from './util/fbCookies'
import { consentStore } from './consent/ConsentStore'
import { getAnalyticsIdentity } from './identity'
import type { ConsentSnapshot } from './types'

const PURCHASE_EVENT_ID_KEY = 'hh.analytics.purchaseEventId'

export interface AnalyticsContextPayload {
  event_id: string
  external_id?: string
  email?: string
  phone?: string
  fbp?: string
  fbc?: string
  ga_client_id?: string
  event_source_url: string
  consent: ConsentSnapshot
}

/**
 * Build the analytics context sent with POST /api/ecom/user/orders.
 *
 * Backend persists this in order_analytics_context and replays it when the
 * order transitions to PAYMENT_AUTHORIZED (Monobank webhook), emitting the
 * authoritative server-side `purchase` event.
 *
 * Also stashes event_id in sessionStorage so the browser-side purchase on the
 * success page can reuse it — vendors dedupe by event_id.
 */
export function buildPurchaseAnalyticsContext(): AnalyticsContextPayload {
  const eventId = uuidv4()
  try {
    sessionStorage.setItem(PURCHASE_EVENT_ID_KEY, eventId)
  } catch {
    // sessionStorage disabled (e.g. Safari private mode) — proceed without stash;
    // server-side purchase still works, only browser-side dedup is lost.
  }

  const { fbp, fbc } = readFbCookies()
  const identity = getAnalyticsIdentity()

  return {
    event_id: eventId,
    external_id: identity.external_id,
    email: identity.email,
    phone: identity.phone,
    fbp,
    fbc,
    ga_client_id: readGaClientId(),
    event_source_url: typeof window !== 'undefined' ? window.location.href : '',
    consent: consentStore.snapshot(),
  }
}

/** Reads and clears the stashed purchase event_id. Returns undefined if absent. */
export function consumePurchaseEventId(): string | undefined {
  try {
    const id = sessionStorage.getItem(PURCHASE_EVENT_ID_KEY) ?? undefined
    if (id) sessionStorage.removeItem(PURCHASE_EVENT_ID_KEY)
    return id
  } catch {
    return undefined
  }
}
